import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import config from "./config";
import PoweredByFlexiFunnels from "./PoweredByFlexiFunnels";
import PageLoader from "./PageLoader";
import { useBrand } from "./useBrand";
import Tooltip from "./Tooltip";
import CourseOfferModal from "./CourseOfferModal";
import { asset } from "./assetUrl";
import { BrandLogo } from "./MembershipHeader";
import { withReferral } from "./affiliateReferral";

// Same fallback cover the course grid uses. Routed through asset(): a bare
// /images/... would resolve against the CUSTOMER's domain, which answers 405.
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");

// Compact member-count label: 80500 -> "80.5k", 3300 -> "3.3k".
function fmtCount(n) {
  const v = Number(n) || 0;
  if (v >= 1000) {
    const k = v / 1000;
    return `${k % 1 === 0 ? k : k.toFixed(1)}k`;
  }
  return String(v);
}

/**
 * Instructor details page — opened from any "By <instructor>" chip. Lives on the
 * same /:token route, addressed by ?instructor={id} so a refresh or a shared link
 * lands back here.
 *
 * An instructor is credited on BOTH courses and groups, so this page lists both.
 * `focus` decides which list leads: the visitor arrived from a course card or
 * from a group card, and the page opens on the same kind of thing rather than
 * always leading with groups. An empty list is dropped entirely instead of
 * showing a second empty box.
 *
 * LAYOUT: one wide column, not a list + sidebar. The profile is a full-width
 * hero (brand cover band, the avatar straddling its edge, then a hairline stat
 * strip) and each list is a card grid underneath it — so the page reads as a
 * profile with a body of work rather than a table with a business card beside
 * it, and the covers get room to actually be seen.
 *
 * (Activity heatmap / follower stats are intentionally omitted — no backing
 * data.)
 */

// Access type: 1 = Public, 2 = Private, 3 = Paid.
const accessTypeLabel = (t) =>
  Number(t) === 2 ? "Private" : Number(t) === 3 ? "Paid" : "Public";

// A course gate is its own thing: course_access_data.access_type is
// free | paid | level, not the group's public/private.
const courseAccessLabel = (t) =>
  t === "paid" ? "Paid" : t === "level" ? "Level" : "Free";

// The gate chip sits in the card BODY, not over the picture, so it is a solid
// tinted pill rather than the white blurred chip artwork needs. "Level" is the
// exception with no entry here: it names something earned inside THIS
// membership, so it takes the brand colour, applied inline — the colour isn't
// known until the brand loads.
const ACCESS_CHIP = {
  Public: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30",
  Free: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30",
  Paid: "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30",
  Private: "bg-slate-100 text-slate-600 ring-slate-200",
};

// Social platform icons (rendered for each link stored on the instructor).
// Order controls how they appear in the row.
const SOCIAL_ORDER = ["website", "instagram", "twitter", "youtube", "linkedin", "facebook", "threads", "pinterest", "telegram", "whatsapp", "discord"];
const SOCIAL_ICONS = {
  threads: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M12 2a10 10 0 1 0 6.3 17.8l-1.3-1.5A8 8 0 1 1 20 12v1.2c0 1.2-.8 2-1.8 2s-1.7-.8-1.7-2V12a4.5 4.5 0 1 0-1.4 3.3c.6 1.1 1.8 1.7 3.1 1.7 2.1 0 3.8-1.6 3.8-4V12A10 10 0 0 0 12 2zm0 12.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
    </svg>
  ),
  pinterest: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.4-5.96s-.36-.72-.36-1.78c0-1.66.97-2.9 2.17-2.9 1.02 0 1.52.77 1.52 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.17 1.78 2.17 2.13 0 3.77-2.25 3.77-5.5 0-2.87-2.06-4.88-5.01-4.88-3.41 0-5.42 2.56-5.42 5.21 0 1.03.4 2.14.89 2.74.1.12.11.22.08.34l-.33 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.39 2.97 7.39 6.92 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z" />
    </svg>
  ),
  telegram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M11.94 0A12 12 0 1 0 24 12 12 12 0 0 0 11.94 0zm5.56 8.16l-1.97 9.29c-.15.66-.54.82-1.09.51l-3-2.21-1.45 1.39c-.16.16-.29.29-.6.29l.21-3.05 5.56-5.02c.24-.21-.05-.33-.37-.12l-6.87 4.33-2.96-.92c-.64-.2-.66-.64.14-.95l11.57-4.46c.54-.2 1.01.13.83.94z" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.05 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88zm8.41-18.3A11.81 11.81 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89a11.82 11.82 0 0 0-3.48-8.41z" />
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M20.32 4.37a19.8 19.8 0 0 0-4.89-1.52.07.07 0 0 0-.08.04c-.21.38-.44.87-.61 1.25a18.27 18.27 0 0 0-5.49 0 12.64 12.64 0 0 0-.62-1.25.08.08 0 0 0-.08-.04 19.74 19.74 0 0 0-4.89 1.52.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06a.08.08 0 0 0 .03.06 19.9 19.9 0 0 0 5.99 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.23-1.99a.08.08 0 0 0-.04-.11 13.1 13.1 0 0 1-1.87-.89.08.08 0 0 1-.01-.13l.37-.29a.07.07 0 0 1 .08-.01c3.93 1.79 8.18 1.79 12.06 0a.07.07 0 0 1 .08.01l.37.29a.08.08 0 0 1-.01.13c-.6.35-1.22.65-1.87.89a.08.08 0 0 0-.04.11c.36.7.78 1.36 1.23 1.99a.08.08 0 0 0 .08.03 19.84 19.84 0 0 0 6-3.03.08.08 0 0 0 .03-.06c.5-5.18-.84-9.67-3.55-13.66a.06.06 0 0 0-.03-.03zM8.02 15.33c-1.18 0-2.16-1.09-2.16-2.42 0-1.33.96-2.42 2.16-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.33-.96 2.42-2.16 2.42zm7.97 0c-1.18 0-2.15-1.09-2.15-2.42 0-1.33.95-2.42 2.15-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.33-.95 2.42-2.16 2.42z" />
    </svg>
  ),
  website: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-[17px] h-[17px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5a4 4 0 010 5.66l-2.5 2.5a4 4 0 01-5.66-5.66l1.5-1.5M10.5 13.5a4 4 0 010-5.66l2.5-2.5a4 4 0 015.66 5.66l-1.5 1.5" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 00-2.13 1.38A5.9 5.9 0 00.63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 002.13-1.38 5.9 5.9 0 001.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 00-1.38-2.13A5.9 5.9 0 0019.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1018.16 12 6.16 6.16 0 0012 5.84zm0 10.16A4 4 0 1116 12a4 4 0 01-4 4zm6.41-11.85a1.44 1.44 0 11-1.44-1.44 1.44 1.44 0 011.44 1.44z" />
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.4l-5.8-7.58-6.63 7.58H.49l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z" />
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31 31 0 000 12a31 31 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31 31 0 0024 12a31 31 0 00-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M19 0h-14C2.24 0 0 2.24 0 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5zM7.12 20.45H3.56V9h3.56v11.45zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]">
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z" />
    </svg>
  ),
};

/* ── small building blocks ──────────────────────────────────────────────── */

function MetaIcon({ d, className = "w-[15px] h-[15px]" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const ICON = {
  members: "M17 20h5v-1a4 4 0 00-3-3.87M9 20H4v-1a4 4 0 013-3.87m6-6a3 3 0 11-6 0 3 3 0 016 0zm6 1a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z",
  lessons: "M12 6.5A6 6 0 003 5v13a6 6 0 019 1.5A6 6 0 0121 18V5a6 6 0 00-9 1.5zm0 0V20",
  modules: "M4 6h16M4 12h16M4 18h10",
  pin: "M15 10.5a3 3 0 11-6 0 3 3 0 016 0zM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z",
  calendar: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5",
};

/** A meta chip in a card footer: icon + text, muted. */
function Meta({ d, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-500">
      <MetaIcon d={d} className="w-[15px] h-[15px] text-gray-400" />
      {children}
    </span>
  );
}

/** Star + score, used on a course cover. */
function RatingChip({ value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-surface/95 px-2.5 py-1 text-[11.5px] font-bold text-amber-600 dark:text-amber-300 shadow-sm backdrop-blur-sm">
      <svg className="w-3.5 h-3.5 fill-amber-400" viewBox="0 0 24 24">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
      {Number(value).toFixed(1)}
    </span>
  );
}

export default function InstructorDetails({ token, instructorId, member, onView, onViewCourse, onLogin, onBack, focus = "group" }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // A creator can write a bio of any length. The card clamps it and offers
  // "Read more", which opens the full text in its own panel.
  const [bioOpen, setBioOpen] = useState(false);
  // The course whose offer chooser is open, when it is sold by several products.
  const [offerCourse, setOfferCourse] = useState(null);
  const [bioClipped, setBioClipped] = useState(false);
  const bioRef = useRef(null);

  useEffect(() => {
    let on = true;
    setLoading(true);
    setError("");
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-instructor`, {
        community_token: token,
        instructor_id: instructorId,
        // Optional: with it the API can tell which paid courses this member has
        // already bought. Without it (a guest) every paid course reads unbought,
        // which is the right answer — they get the sales page.
        member_token: member?.member_token || "",
      })
      .then((res) => {
        if (!on) return;
        if (res.data?.error === 0) setData(res.data);
        else setError(res.data?.msg || "Instructor not found.");
      })
      .catch(() => on && setError("Something went wrong."))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [token, instructorId, member?.member_token]);

  // Whether the clamp actually cut anything — measured, not guessed from the
  // character count, because how much fits depends on the column width. Re-run
  // on resize for the same reason.
  useEffect(() => {
    const measure = () => {
      const el = bioRef.current;
      setBioClipped(el ? el.scrollHeight - el.clientHeight > 2 : false);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [data]);

  // Escape closes the panel.
  //
  // Background scroll is locked GLOBALLY by <ScrollLockManager/>, which spots any
  // `fixed inset-0` overlay at z-index >= 40 — this panel qualifies. Locking it
  // here as well left the page frozen after closing: the manager reads
  // body.style.overflow to remember what to restore, and on open it read the
  // "hidden" this effect had just written. Closing then restored "hidden"
  // forever. MembershipHeader carries the same warning over its drawer.
  useEffect(() => {
    if (!bioOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setBioOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bioOpen]);

  // Brand from the login page's source (getfunnel). Called before the early
  // returns below so hook order stays stable.
  const { primary, secondary } = useBrand(token);

  if (loading) {
    return <PageLoader page="Instructor" primary={primary} />;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f7f9] dark:bg-canvas px-4">
        <div className="w-full max-w-sm rounded-2xl border border-gray-200/80 bg-surface px-6 py-10 text-center shadow-sm">
          <p className="text-sm font-semibold text-gray-500">{error || "Not found"}</p>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-surface px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              Go back
            </button>
          )}
        </div>
      </div>
    );
  }

  const { community, instructor, groups, courses = [] } = data;
  const firstName = (instructor.name || "").trim().split(/\s+/)[0] || instructor.name;
  const groupCount = groups.length;
  const courseCount = courses.length;
  const totalMembers = groups.reduce((s, g) => s + (Number(g.member_count) || 0), 0);
  const totalLessons = courses.reduce((s, c) => s + (Number(c.lesson_count) || 0), 0);
  // Which list leads — see `focus` in the block comment above.
  const coursesFirst = focus === "course";

  /* ── brand-derived surfaces ───────────────────────────────────────────────
     colour-mix keeps these robust for any hex/rgb the creator picked. The cover
     runs primary -> a darkened primary so white type stays legible whatever the
     brand is; the creator's SECOND colour only ever appears as a corner glow,
     for the same reason — a pale secondary as the base would wash the type out. */
  const coverBg = [
    `radial-gradient(120% 150% at 8% 0%, color-mix(in srgb, ${primary} 45%, #fff) 0%, transparent 55%)`,
    `radial-gradient(90% 130% at 100% 105%, ${
      secondary ? `color-mix(in srgb, ${secondary} 65%, transparent)` : "rgba(255,255,255,0.22)"
    } 0%, transparent 62%)`,
    `linear-gradient(115deg, ${primary} 0%, color-mix(in srgb, ${primary} 58%, #000) 100%)`,
  ].join(", ");
  // Chip ground: a whisper of the brand over the CARD surface, not over a
  // literal white — mixing with white left these chips glowing on a dark page
  // while their brand-coloured text stayed dark.
  const softBrand = `color-mix(in srgb, ${primary} 8%, rgb(var(--c-surface)))`;
  const brandButton = { backgroundColor: primary, color: "var(--brand-btn-text, #fff)" };

  /* ── shared card chrome ───────────────────────────────────────────────────
     One card per row, image on the left and the detail on the right. It stacks
     to image-on-top below `sm`, where a side-by-side split would squeeze the
     picture into a sliver and the text into two words a line. */
  const cardClass =
    "group flex flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-surface shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_18px_40px_-22px_rgba(15,23,42,0.38)] sm:h-44 sm:flex-row";

  // Fills the card's full height on a row, so every card in a stack lines up no
  // matter how much text sits beside it.
  const cover = (src, alt, fallback) => (
    <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-gray-100 sm:aspect-auto sm:h-full sm:w-60">
      <img
        src={src || fallback}
        alt={alt}
        onError={fallback ? (e) => { e.currentTarget.src = fallback; } : undefined}
        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
      />
    </div>
  );

  const accessChip = (label) => (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${ACCESS_CHIP[label] || "ring-transparent"}`}
      style={ACCESS_CHIP[label] ? undefined : { backgroundColor: softBrand, color: primary }}
    >
      {label}
    </span>
  );

  const cta = (onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      style={brandButton}
      className="group/btn inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-bold shadow-sm transition hover:opacity-90 active:scale-[0.98]"
    >
      {label}
      <svg className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );

  const sectionHead = (eyebrow, title, count) => (
    <div className="mb-5">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.15em]" style={{ color: primary }}>
        {eyebrow}
      </p>
      <div className="mt-1 flex items-center gap-2.5">
        <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">{title}</h2>
        <span
          className="inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-extrabold"
          style={{ backgroundColor: softBrand, color: primary }}
        >
          {count}
        </span>
      </div>
    </div>
  );

  // Missing means ON, for the same reason it does in nativeLearning: an older
  // API build omits the key, and reading that as "hidden" would strip the rating
  // from every membership the moment this shipped ahead of the API.
  const ratingOn = data?.rating_mode !== 0;

  const listClass = "space-y-4";

  /* ── courses ───────────────────────────────────────────────────────────────
     A paid course the visitor has not bought goes to its SALES PAGE instead of
     the player — the same rule the course grid applies (openCourse in
     CourseViews). A guest is never "purchased", so they get the sales page too.
     Free and level courses always open; a level course they haven't reached
     covers itself with its own lock screen, which explains the gate. */
  const courseGoesToSales = (c) => !c.purchased && !!c.sales_url;
  // Several products sell this course, each with its own page and price — offer
  // the choice rather than picking one, the same as the course grid and the
  // members-page gate. Empty for a single-product course, which is unchanged.
  const courseOffers = (c) => (Array.isArray(c.offers) ? c.offers : []);
  const openCourseCard = (c) => {
    if (courseGoesToSales(c)) {
      if (courseOffers(c).length > 1) {
        setOfferCourse(c);
        return;
      }
      window.open(withReferral(c.sales_url), "_blank", "noopener,noreferrer");
      return;
    }
    if (onViewCourse) onViewCourse(c);
  };

  const coursesSection = courseCount === 0 ? null : (
    <section key="courses">
      {offerCourse ? (
        <CourseOfferModal
          courseName={offerCourse.name}
          offers={offerCourse.offers}
          primary={primary}
          onClose={() => setOfferCourse(null)}
        />
      ) : null}
      {sectionHead("Courses", `Taught by ${firstName}`, courseCount)}
      <div className={listClass}>
        {courses.map((c) => {
          const access = courseAccessLabel(c.access_type);
          const toSales = courseGoesToSales(c);
          return (
            <article key={c.course_token} className={cardClass}>
              {cover(c.image, c.name, DEFAULT_COURSE_IMAGE)}
              <div className="flex min-w-0 flex-1 flex-col p-5">
                <div className="flex items-start gap-3">
                  <h3 className="min-w-0 flex-1 text-[16.5px] font-bold leading-snug text-gray-900 line-clamp-1" title={c.name}>
                    {c.name}
                  </h3>
                  {/* Gate and rating ride with the title. Neither goes on the
                      picture: a chip over artwork has to be white-on-blur to stay
                      legible, which reads as a watermark rather than a label. */}
                  {accessChip(access)}
                  {/* Course Rating toggle (admin: Settings → Features). This page
                      has its own copy of the flag — the catalogue's answer never
                      reaches it — so the setting holds here too. */}
                  {ratingOn && Number(c.star_rating) > 0 && <RatingChip value={c.star_rating} />}
                </div>
                {c.description && (
                  <Tooltip label={c.description}>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-gray-500 line-clamp-2 break-words">
                      {c.description}
                    </p>
                  </Tooltip>
                )}
                <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
                    <Meta d={ICON.lessons}>
                      {c.lesson_count} {Number(c.lesson_count) === 1 ? "lesson" : "lessons"}
                    </Meta>
                    {c.module_count > 0 && (
                      <Meta d={ICON.modules}>
                        {c.module_count} {Number(c.module_count) === 1 ? "module" : "modules"}
                      </Meta>
                    )}
                  </div>
                  {/* "Enroll" promises one destination; with several plans behind
                      it the click opens a chooser, so the label says so. */}
                  {(onViewCourse || toSales) && cta(
                    () => openCourseCard(c),
                    toSales ? (courseOffers(c).length > 1 ? "See plans" : "Enroll") : "View",
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );

  /* ── groups ─────────────────────────────────────────────────────────────── */
  const groupsSection = groupCount === 0 ? null : (
    <section key="groups">
      {sectionHead("Community", `Communities hosted by ${firstName}`, groupCount)}
      <div className={listClass}>
        {groups.map((g) => {
          const access = accessTypeLabel(g.access_type);
          return (
            <article key={g.group_id} className={cardClass}>
              {cover(g.banner, g.name, null)}
              <div className="flex min-w-0 flex-1 flex-col p-5">
                <div className="flex items-start gap-3">
                  <h3 className="min-w-0 flex-1 text-[16.5px] font-bold leading-snug text-gray-900 line-clamp-1" title={g.name}>
                    {g.name}
                  </h3>
                  {accessChip(access)}
                  {g.price_label && (
                    <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11.5px] font-bold text-gray-600 ring-1 ring-inset ring-gray-200">
                      {g.price_label}
                    </span>
                  )}
                </div>
                {g.description && (
                  <Tooltip label={g.description}>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-gray-500 line-clamp-2 break-words">
                      {g.description}
                    </p>
                  </Tooltip>
                )}
                <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                  <Meta d={ICON.members}>
                    {fmtCount(g.member_count)} {Number(g.member_count) === 1 ? "member" : "members"}
                  </Meta>
                  {onView && cta(() => onView(g), "View")}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );

  // Only the tiles with something behind them: a course-only instructor should
  // not be introduced by two zeroes.
  const stats = [
    ...(courseCount ? [[courseCount, courseCount === 1 ? "Course" : "Courses"], [fmtCount(totalLessons), "Lessons"]] : []),
    ...(groupCount ? [[groupCount, groupCount === 1 ? "Community" : "Communities"], [fmtCount(totalMembers), "Members"]] : []),
  ];

  return (
    <div className="min-h-screen bg-[#f6f7f9] dark:bg-canvas">
      {/* header — logo + Back / Log In */}
      <header className="sticky top-0 z-20 border-b border-gray-200/80 bg-surface/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1240px] items-center justify-between px-5">
          <div className="flex min-w-0 items-center gap-4">
            <BrandLogo community={community} className="h-8 w-auto max-w-[140px]" />
          </div>
          <div className="flex items-center gap-2.5">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-surface py-2 pl-3 pr-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98]"
              >
                <svg className="h-4 w-4 text-gray-400 transition group-hover:-translate-x-0.5 group-hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            )}
            {!member && (
              <button
                onClick={onLogin}
                style={brandButton}
                className="rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition hover:opacity-90 active:scale-[0.98]"
              >
                Log In
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1240px] px-5 py-7 sm:py-9">
        {/* Two columns: the work on the left, the profile parked on the right.
            items-start is what lets the aside go sticky — a stretched flex item
            has nothing to scroll within. On mobile the profile comes FIRST
            (order-first): you should know whose page this is before the list. */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-7">
          {/* ---- left: courses + groups, led by `focus` ---- */}
          <div className="order-last w-full min-w-0 space-y-10 lg:order-first lg:flex-1">
            {courseCount === 0 && groupCount === 0 ? (
              <div className="rounded-3xl border border-dashed border-gray-300 bg-surface/60 px-6 py-16 text-center">
                <span
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: softBrand, color: primary }}
                >
                  <MetaIcon d={ICON.lessons} className="w-6 h-6" />
                </span>
                <p className="mt-4 text-[15px] font-bold text-gray-700">Nothing published yet</p>
                <p className="mt-1 text-sm text-gray-400">
                  {firstName} isn't linked to any course or community in this membership.
                </p>
              </div>
            ) : coursesFirst ? (
              <>
                {coursesSection}
                {groupsSection}
              </>
            ) : (
              <>
                {groupsSection}
                {coursesSection}
              </>
            )}
          </div>

          {/* ---- right: the profile card ---- */}
          <aside className="order-first w-full shrink-0 lg:order-last lg:sticky lg:top-20 lg:w-[344px]">
            <div className="overflow-hidden rounded-3xl border border-gray-200/70 bg-surface shadow-[0_10px_40px_-24px_rgba(15,23,42,0.35)]">
              {/* brand cover band + dot texture */}
              <div className="relative h-28" style={{ backgroundImage: coverBg }}>
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage: "radial-gradient(rgba(255,255,255,0.22) 1px, transparent 1px)",
                    backgroundSize: "16px 16px",
                    maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)",
                    WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)",
                  }}
                />
              </div>

              {/* relative: the band above is positioned, so without its own
                  stacking context this block's overlap paints UNDER it and the
                  avatar loses its top half. */}
              <div className="relative -mt-14 px-6 pb-6">
                <div className="flex justify-center">
                  {instructor.profile_image ? (
                    <img
                      src={instructor.profile_image}
                      alt={instructor.name}
                      className="h-28 w-28 rounded-2xl object-cover ring-4 ring-surface shadow-lg"
                    />
                  ) : (
                    <div
                      className="flex h-28 w-28 items-center justify-center rounded-2xl text-4xl font-extrabold ring-4 ring-surface shadow-lg"
                      style={brandButton}
                    >
                      {instructor.name ? instructor.name.charAt(0).toUpperCase() : "?"}
                    </div>
                  )}
                </div>

                <h1 className="mt-4 text-center text-[21px] font-extrabold leading-tight tracking-tight text-gray-900">
                  {instructor.name}
                </h1>
                <p
                  className="mt-1.5 text-center text-[10.5px] font-extrabold uppercase tracking-[0.16em]"
                  style={{ color: primary }}
                >
                  Instructor
                </p>

                {instructor.short_description && (
                  <>
                    <p
                      ref={bioRef}
                      className="mt-4 line-clamp-4 whitespace-pre-line text-center text-[13.5px] leading-relaxed text-gray-600"
                    >
                      {instructor.short_description}
                    </p>
                    {bioClipped && (
                      <button
                        type="button"
                        onClick={() => setBioOpen(true)}
                        className="mx-auto mt-2 flex items-center gap-1 text-[12.5px] font-bold transition hover:opacity-80"
                        style={{ color: primary }}
                      >
                        Read more
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* stats — gap-px over a grey ground draws the hairlines, so the
                  block stays clean at either tile count and on any wrap */}
              {stats.length > 0 && (
                <div className="grid grid-cols-2 gap-px border-y border-gray-100 bg-gray-100">
                  {stats.map(([value, label]) => (
                    <div key={label} className="bg-surface px-4 py-3.5 text-center">
                      <div className="text-[19px] font-extrabold tracking-tight text-gray-900">{value}</div>
                      <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400">
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="px-6 py-5">
                {(instructor.location || instructor.joined) && (
                  // One inset panel, hairline-divided. A labelled row (what it is
                  // above what it says) carries far better than icon-plus-text,
                  // where the icon is the only thing naming the value.
                  <div className="divide-y divide-gray-200/70 overflow-hidden rounded-2xl bg-gray-50/80 ring-1 ring-inset ring-gray-200/70">
                    {[
                      instructor.location && ["Location", instructor.location, ICON.pin],
                      instructor.joined && ["Joined", instructor.joined, ICON.calendar],
                    ]
                      .filter(Boolean)
                      .map(([label, value, icon]) => (
                        <div key={label} className="flex items-start gap-3 px-3.5 py-3">
                          <span
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_2px_rgba(16,24,40,0.06)] ring-1 ring-inset ring-surface/60"
                            style={{ backgroundColor: softBrand, color: primary }}
                          >
                            <MetaIcon d={icon} className="w-[18px] h-[18px]" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-gray-400">
                              {label}
                            </p>
                            <p className="mt-0.5 text-[13px] font-semibold leading-snug text-gray-700">
                              {value}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {instructor.social_links && Object.keys(instructor.social_links).length > 0 && (
                  <>
                    {(instructor.location || instructor.joined) && <hr className="my-4 border-gray-100" />}
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      {SOCIAL_ORDER.filter((k) => instructor.social_links[k]).map((k) => (
                        <a
                          key={k}
                          href={instructor.social_links[k]}
                          target="_blank"
                          rel="noreferrer"
                          title={k}
                          className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/70 transition-all duration-200 hover:-translate-y-0.5 hover:text-white hover:shadow-sm"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = primary;
                            e.currentTarget.style.boxShadow = `0 4px 10px -2px color-mix(in srgb, ${primary} 45%, transparent)`;
                            e.currentTarget.style.setProperty("--tw-ring-color", primary);
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "";
                            e.currentTarget.style.boxShadow = "";
                            e.currentTarget.style.removeProperty("--tw-ring-color");
                            e.currentTarget.style.color = "";
                          }}
                        >
                          {SOCIAL_ICONS[k]}
                        </a>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {community?.show_branding && <PoweredByFlexiFunnels className="mt-4" />}
          </aside>

          {/* Full bio. A bottom sheet on phones and a centred dialog from `sm`
              up — the same panel either way, only anchored where the thumb is.
              Portalled to <body> so the sticky aside can't clip it, and its own
              scroll is capped at 60vh rather than growing the page. */}
          {bioOpen &&
            createPortal(
              <div
                className="fixed inset-0 z-[60] flex items-end justify-center bg-ink-slate/50 backdrop-blur-sm sm:items-center sm:p-4"
                onClick={() => setBioOpen(false)}
                role="presentation"
              >
                <div
                  className="w-full max-w-lg overflow-hidden rounded-t-3xl bg-surface shadow-2xl sm:rounded-3xl"
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                  aria-label={`About ${instructor.name}`}
                >
                  <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
                    {instructor.profile_image ? (
                      <img src={instructor.profile_image} alt="" className="h-10 w-10 shrink-0 rounded-xl object-cover" />
                    ) : (
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-base font-extrabold"
                        style={brandButton}
                      >
                        {instructor.name ? instructor.name.charAt(0).toUpperCase() : "?"}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-extrabold tracking-tight text-gray-900">
                        {instructor.name}
                      </p>
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: primary }}>
                        About
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBioOpen(false)}
                      aria-label="Close"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200/70 transition hover:bg-gray-100 hover:text-gray-700"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto px-5 py-5">
                    <p className="whitespace-pre-line text-[14.5px] leading-relaxed text-gray-600">
                      {instructor.short_description}
                    </p>
                  </div>
                </div>
              </div>,
              document.body,
            )}
        </div>
      </div>
    </div>
  );
}
